import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDepartments } from "../../../hooks/useDepartments";
import { sessionService } from "../../../services/sessionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import { EmptyState } from "../../common/EmptyState";
import UniversalFilters from "../../common/UniversalFilters";
import ExportPDFButton from "../../common/ExportPDFButton";
import ExportExcelButton from "../../common/ExportExcelButton";
import { getFilterConfig } from "../../../constants/filterConfigs";
import { useFilters } from "../../../hooks/useFilters";
import { formatSessionAmount } from "../../../constants/sessionConstants";
import { getSectorLabel } from "../../../constants/authConstants";
import { financialYearUtils } from "../../../utils/helpers";
import { exportSessionBudgetDepartmentWisePDF } from "../../../utils/pdfExport";
import { exportSessionBudgetToExcel } from "../../../utils/excelExport";

const SessionBudget = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const {
    departments,
    loading: loadingDepartments,
    getDepartmentLabel,
  } = useDepartments();

  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const { filters, handleFilterChange, clearFilters, hasActiveFilters } =
    useFilters({
      search: "",
      sector: "",
      department: "",
      financialYear: "",
    });

  const filterConfig = getFilterConfig("SESSION_BUDGET");

  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const cardPadding = getResponsiveValue({
    mobile: "p-4",
    desktop: "p-6",
  });

  useEffect(() => {
    if (departments.length > 0) {
      loadSessionData();
    }
  }, [filters.financialYear, filters.sector, filters.department, departments]);

  useEffect(() => {
    applyFilters();
  }, [sessionData, filters.search, departments]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await sessionService.getSessionProjects(
        filters.financialYear,
      );

      if (result.success) {
        const sessionProjects = result.data;

        // Filter by sector if selected
        let filteredProjects = filters.sector
          ? sessionProjects.filter((project) => {
              const dept = departments.find(
                (d) => d.departmentCode === project.department,
              );
              return dept?.sector === filters.sector;
            })
          : sessionProjects;

        // Filter by department if selected
        if (filters.department) {
          filteredProjects = filteredProjects.filter(
            (project) => project.department === filters.department
          );
        }

        // Calculate department totals from session projects
        const departmentTotals =
          calculateSessionDepartmentTotals(filteredProjects);
        const sectorTotals = calculateSessionSectorTotals(departmentTotals);

        setSessionData({
          departments: departmentTotals,
          sectorTotals,
          projects: filteredProjects,
          totalProjects: filteredProjects.length,
        });
      }
    } catch (error) {
      console.error("Error loading session data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!sessionData || departments.length === 0) {
      setFilteredData(null);
      return;
    }

    let filteredDepartments = [...sessionData.departments];

    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filteredDepartments = filteredDepartments.filter((dept) => {
        const deptLabel = getDepartmentLabel(dept.department).toLowerCase();
        const deptObj = departments.find(
          (d) => d.departmentCode === dept.department,
        );
        const sectorLabel = deptObj
          ? getSectorLabel(deptObj.sector).toLowerCase()
          : "";
        return (
          deptLabel.includes(searchLower) || sectorLabel.includes(searchLower)
        );
      });
    }

    // Sort departments by priority globally
    filteredDepartments = filteredDepartments.sort((a, b) => {
      const deptA = departments.find((d) => d.departmentCode === a.department);
      const deptB = departments.find((d) => d.departmentCode === b.department);

      const priorityA = deptA?.priority || 999;
      const priorityB = deptB?.priority || 999;

      return priorityA - priorityB;
    });

    // Recalculate sector totals for filtered data
    const filteredSectorTotals =
      calculateSessionSectorTotals(filteredDepartments);

    setFilteredData({
      ...sessionData,
      departments: filteredDepartments,
      sectorTotals: filteredSectorTotals,
    });
  };

  // Calculate department totals from session projects
  const calculateSessionDepartmentTotals = (sessionProjects) => {
    const departmentTotals = {};

    sessionProjects.forEach((project) => {
      if (!project.department) return;

      const dept = departments.find(
        (d) => d.departmentCode === project.department,
      );
      const sector = dept?.sector || "Unknown";

      if (!departmentTotals[project.department]) {
        departmentTotals[project.department] = {
          department: project.department,
          sector: sector,
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
        };
      }

      const deptData = departmentTotals[project.department];
      const amount = parseFloat(project.amount) || 0;

      deptData.approvedTotal += amount;

      if (project.clearanceType === "state_plan") {
        deptData.approvedStatePlan += amount;
      } else if (project.clearanceType === "negotiated_loan") {
        deptData.approvedNegotiatedLoan += amount;
      }
    });

    return Object.values(departmentTotals);
  };

  // Calculate sector totals from department data
  const calculateSessionSectorTotals = (departmentData) => {
    const sectorTotals = {};

    departmentData.forEach((dept) => {
      if (!sectorTotals[dept.sector]) {
        sectorTotals[dept.sector] = {
          sector: dept.sector,
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
          departmentCount: 0,
        };
      }

      sectorTotals[dept.sector].approvedTotal += dept.approvedTotal || 0;
      sectorTotals[dept.sector].approvedStatePlan +=
        dept.approvedStatePlan || 0;
      sectorTotals[dept.sector].approvedNegotiatedLoan +=
        dept.approvedNegotiatedLoan || 0;
      sectorTotals[dept.sector].departmentCount += 1;
    });

    return sectorTotals;
  };

  // Group departments by sector, maintaining global priority order
  const getGroupedDepartments = () => {
    if (!filteredData?.departments) return { grouped: {}, sectorOrder: [] };

    // Departments are already sorted by priority in applyFilters
    const sortedDepartments = filteredData.departments;

    const grouped = {};
    const sectorOrder = [];

    sortedDepartments.forEach((dept) => {
      if (!grouped[dept.sector]) {
        grouped[dept.sector] = [];
        sectorOrder.push(dept.sector);
      }
      grouped[dept.sector].push(dept);
    });

    return { grouped, sectorOrder };
  };

  const handleExportPDF = () => {
    try {
      setExporting(true);

      // Prepare data for export
      const exportData = {
        departments: filteredData.departments.map((dept) => ({
          ...dept,
          departmentName: getDepartmentLabel(dept.department),
        })),
      };

      exportSessionBudgetDepartmentWisePDF(exportData, filters);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = () => {
    try {
      setExportingExcel(true);

      exportSessionBudgetToExcel(filteredData, filters, getDepartmentLabel);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      setError("Failed to export Excel. Please try again.");
    } finally {
      setExportingExcel(false);
    }
  };

  const { grouped: groupedDepartments, sectorOrder } = getGroupedDepartments();
  const sectorsToShow = filters.sector ? [filters.sector] : sectorOrder;

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading session budget..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        .table-container {
          max-height: calc(100vh - 200px);
          overflow-y: auto;
          position: relative;
        }
        
        .sticky-header thead {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .sticky-header thead th {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-2`}>
            Department Wise
          </h1>
          <p className="text-text-secondary">
            Department-wise budget allocation summary for session projects
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <ExportExcelButton
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={!filteredData || filteredData.departments.length === 0}
          />

          <ExportPDFButton
            onClick={handleExportPDF}
            loading={exporting}
            disabled={!filteredData || filteredData.departments.length === 0}
          />

          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-error mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Session Budget Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {sectorsToShow.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg
                  className="w-16 h-16 text-text-quaternary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              }
              title={
                filters.search
                  ? "No departments match your search"
                  : "No session projects found"
              }
              description={
                filters.search
                  ? `No departments found matching "${filters.search}". Try adjusting your search terms.`
                  : "No session projects available for the selected filters"
              }
              actionLabel="Clear Filters"
              onAction={clearFilters}
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="min-w-full text-xs sticky-header">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S.No.
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 bg-blue-50 w-40">
                    Total Allocation
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 bg-blue-50 w-40">
                    State Plan
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider bg-blue-50 w-40">
                    Negotiated Loan
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sectorsToShow.map((sectorKey) => {
                  const depts = groupedDepartments[sectorKey] || [];
                  const sectorTotal = filteredData.sectorTotals[sectorKey];
                  let serialCounter = 1;

                  if (depts.length === 0) return null;

                  return (
                    <React.Fragment key={sectorKey}>
                      {/* Department rows */}
                      {depts.map((dept) => (
                        <tr
                          key={dept.department}
                          className="hover:bg-blue-50 transition-colors duration-200"
                        >
                          <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                            <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                              {serialCounter++}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200">
                            {getDepartmentLabel(dept.department)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-blue-200 bg-blue-50">
                            {formatSessionAmount(dept.approvedTotal)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-blue-200 bg-blue-50">
                            {formatSessionAmount(dept.approvedStatePlan)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium bg-blue-50">
                            {formatSessionAmount(dept.approvedNegotiatedLoan)}
                          </td>
                        </tr>
                      ))}

                      {/* Sector Total Row */}
                      {sectorTotal && (
                        <tr className="bg-gray-700 text-white font-bold border-t-2 border-gray-600">
                          <td className="px-3 py-3 border-r border-gray-600"></td>
                          <td className="px-3 py-3 text-xs font-bold border-r border-gray-600">
                            Total - {getSectorLabel(sectorKey)} (
                            {sectorTotal.departmentCount} departments)
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                            {formatSessionAmount(sectorTotal.approvedTotal)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                            {formatSessionAmount(sectorTotal.approvedStatePlan)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-bold">
                            {formatSessionAmount(
                              sectorTotal.approvedNegotiatedLoan,
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Grand Total Row */}
                {filteredData &&
                  Object.keys(filteredData.sectorTotals).length > 0 && (
                    <tr className="bg-gray-800 text-white font-bold border-t-4 border-gray-700">
                      <td className="px-3 py-3 border-r border-gray-700"></td>
                      <td className="px-3 py-3 text-xs font-bold border-r border-gray-700">
                        Grand Total
                        {hasActiveFilters && " (Filtered)"}
                      </td>
                      <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                        {formatSessionAmount(
                          Object.values(filteredData.sectorTotals).reduce(
                            (sum, sector) => sum + sector.approvedTotal,
                            0,
                          ),
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                        {formatSessionAmount(
                          Object.values(filteredData.sectorTotals).reduce(
                            (sum, sector) => sum + sector.approvedStatePlan,
                            0,
                          ),
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-sm font-bold">
                        {formatSessionAmount(
                          Object.values(filteredData.sectorTotals).reduce(
                            (sum, sector) =>
                              sum + sector.approvedNegotiatedLoan,
                            0,
                          ),
                        )}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionBudget;